<script>
import Spinner from 'shared/components/Spinner.vue';

export default {
  name: 'SquadBiReportModal',
  components: {
    Spinner,
  },
  props: {
    report: {
      type: Object,
      required: true,
    },
    isVisible: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close'],
  data() {
    return {
      isLoading: true,
      hasError: false,
    };
  },
  watch: {
    isVisible(newValue) {
      if (newValue) {
        this.resetState();
      }
    },
    'report.link'() {
      this.resetState();
    },
  },
  methods: {
    onClose() {
      this.$emit('close');
    },

    openInNewTab() {
      window.open(this.report.link, '_blank', 'noopener,noreferrer');
    },

    refreshReport() {
      this.resetState();
      if (this.$refs.reportFrame) {
        this.$refs.reportFrame.src = this.report.link;
      }
    },

    onIframeLoad() {
      this.isLoading = false;
      this.hasError = false;
    },

    onIframeError() {
      this.isLoading = false;
      this.hasError = true;
    },

    resetState() {
      this.isLoading = true;
      this.hasError = false;
    },
  },
};
</script>

<template>
  <woot-modal :show="isVisible" :on-close="onClose" size="full">
    <template #header>
      <div class="flex items-center justify-between w-full">
        <div>
          <h2 class="text-xl font-semibold text-slate-900">
            {{ report.report }}
          </h2>
          <p class="text-sm text-slate-600 mt-1">
            {{ $t('SQUAD_BI.REPORT_MODAL_SUBTITLE') }}
          </p>
        </div>
        <div class="flex items-center space-x-2">
          <woot-button variant="clear" size="small" @click="openInNewTab">
            <fluent-icon icon="external_link" />
            {{ $t('SQUAD_BI.OPEN_IN_NEW_TAB') }}
          </woot-button>
          <woot-button variant="clear" size="small" @click="refreshReport">
            <fluent-icon icon="arrow_clockwise" />
            {{ $t('SQUAD_BI.REFRESH') }}
          </woot-button>
        </div>
      </div>
    </template>

    <template #body>
      <div class="h-full flex flex-col">
        <div v-if="isLoading" class="flex-1 flex items-center justify-center">
          <div class="text-center">
            <Spinner size="large" />
            <p class="mt-4 text-slate-600">
              {{ $t('SQUAD_BI.LOADING_REPORT') }}
            </p>
          </div>
        </div>

        <div
          v-else-if="hasError"
          class="flex-1 flex items-center justify-center"
        >
          <div class="text-center">
            <fluent-icon
              icon="error_circle"
              class="text-red-500 text-3xl mb-4"
            />
            <h3 class="text-lg font-medium text-slate-900 mb-2">
              {{ $t('SQUAD_BI.REPORT_ERROR_TITLE') }}
            </h3>
            <p class="text-slate-600 mb-4">
              {{ $t('SQUAD_BI.REPORT_ERROR_MESSAGE') }}
            </p>
            <woot-button @click="refreshReport">
              {{ $t('SQUAD_BI.RETRY_BUTTON') }}
            </woot-button>
          </div>
        </div>

        <div v-else class="flex-1 relative">
          <iframe
            ref="reportFrame"
            :src="report.link"
            class="w-full h-full border-0"
            frameborder="0"
            allowfullscreen
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            @load="onIframeLoad"
            @error="onIframeError"
          />

          <div
            v-if="isLoading"
            class="absolute inset-0 bg-white flex items-center justify-center"
          >
            <Spinner size="large" />
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-between items-center w-full">
        <div class="text-sm text-slate-500">
          {{ $t('SQUAD_BI.POWERED_BY_SQUAD_BI') }}
        </div>
        <div class="flex space-x-2">
          <woot-button variant="clear" @click="onClose">
            {{ $t('SQUAD_BI.CLOSE') }}
          </woot-button>
        </div>
      </div>
    </template>
  </woot-modal>
</template>

<style scoped>
.woot-modal--full {
  height: calc(100vh - 4rem);
}

iframe {
  background: white;
}
</style>
